import { apiClient } from '../api/fetchClient';
import type { Membership, Tier } from '../membership/models/membership.model';
import { MembershipHelper } from '../membership/services/membership.helper';
import { membershipService } from '../membership/services/membership.service';
import { MembershipJoinFlowService } from '../membership/services/membership-join-flow.service';
import { modalService } from '../modal/modal.service';
import type { AppDispatch } from '../store';
import { getCurrentUrl } from '../utils/window.utils';
import { NeedAccessModal } from './components/need-access-modal/need-access-modal.component';
import type { NeedAccessReq, NeedAccessResponse } from './need-access.interface';

export interface ProcessNeedAccessOptions {
  request: NeedAccessReq;
  message?: string;
  modalMaxWidth?: number;
  modalBorderRadius?: string;
  modalBorderWidth?: string;
  modalBorderColor?: string;
  dispatch?: AppDispatch;
  openAuthModal?: () => void;
}

export class NeedAccessService {
  // Shared across instances: every component constructs its own service, and
  // a second tap during the round trip must not queue a second modal
  // (SNGL-7251). modalService dedupes the modal itself; this covers the
  // "opening" window before it exists.
  private static requestInFlight = false;

  async processNeedAccessRequest(options: ProcessNeedAccessOptions): Promise<void> {
    if (NeedAccessService.requestInFlight || modalService.isOpen(NeedAccessModal)) {
      return;
    }
    NeedAccessService.requestInFlight = true;

    await this.getNeedAccess(options.request)
      .then((res) => {
        const keyProducts = res.keyProducts
          ?.map((p) => p.product)
          .filter((p) => !p.error)
          .filter((p) => p.type !== 'Membership' && p.type !== 'Fan Subscription');

        const keyMemberships = res.keyMemberships?.filter((membership: Membership) => {
          membership.tiers = membership.tiers.filter(
            (tier: Tier) => !MembershipHelper.isMembershipOrTierSoldOut(membership, tier)
          );
          return membership.tiers.length > 0;
        });

        const freeTiers = keyMemberships?.flatMap((m) => m.tiers).filter((t) => t.freeTier);

        if (freeTiers?.length === 1 && options.dispatch && options.openAuthModal) {
          MembershipJoinFlowService.initiateMembershipJoin(
            {
              tier: freeTiers[0],
              redirectUrl: getCurrentUrl(),
            },
            options.dispatch,
            options.openAuthModal
          );
          return;
        }

        modalService.open({
          component: NeedAccessModal,
          props: {
            contentType: options.request.contentType,
            contentId: options.request.contentId,
            modalTitle: options.message || 'Join to unlock',
            products: keyProducts,
            memberships: keyMemberships,
          },
          maxWidth: options.modalMaxWidth,
          borderRadius: options.modalBorderRadius,
          borderWidth: options.modalBorderWidth,
          borderColor: options.modalBorderColor,
        });
      })
      .catch((error) => console.log('Error in processNeedAccessRequest:', error))
      .finally(() => {
        NeedAccessService.requestInFlight = false;
      });
  }

  async getNeedAccess(request: NeedAccessReq): Promise<NeedAccessResponse> {
    const url = new URL('/apps/single/v3/content/need-access', window.location.origin);

    return apiClient.post<NeedAccessResponse>(url.pathname + url.search, request).then((res) => {
      if (res?.keyMemberships?.length) {
        return membershipService
          .getMembershipProducts(res.keyMemberships)
          .then((memberships: Membership[]) => ({ ...res, keyMemberships: memberships }))
          .catch((err) => res); // Return original response if membership products fail
      }
      return res;
    });
  }
}
